// iOS-style search field + Filters disclosure for the audit viewer.
// The search input targets actor_id (most common lookup). The other
// filters (device, action, date range) live inside a tap-to-expand
// grouped card so the default state is one clean pill, not five form
// inputs stacked vertically.

import { useState } from "react";
import type { AuditFilters } from "../data/audit.ts";
import { ALL_ACTIONS } from "./utils.tsx";

export function FilterBar({
  filters,
  onChange,
}: {
  filters: AuditFilters;
  onChange: (f: AuditFilters) => void;
}) {
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<AuditFilters>) =>
    onChange({ ...filters, ...patch, after: undefined });
  const activeExtraCount =
    (filters.deviceId ? 1 : 0) +
    (filters.action ? 1 : 0) +
    (filters.from ? 1 : 0) +
    (filters.to ? 1 : 0);
  return (
    <div className="px-5 md:px-8 pb-3">
      <div className="flex items-center gap-2">
        <label className="flex-1 flex items-center gap-2 h-10 px-3.5 rounded-full bg-slate-200/60">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden="true"
            className="text-slate-500 shrink-0"
          >
            <circle cx="7" cy="7" r="5" />
            <path d="M11 11l3 3" />
          </svg>
          <input
            type="search"
            className="w-full bg-transparent outline-none text-[15px] placeholder:text-slate-500"
            placeholder="Search actor id"
            value={filters.actorId ?? ""}
            onChange={(e) => set({ actorId: e.target.value || undefined })}
          />
        </label>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={`h-10 px-4 rounded-full text-[14px] font-medium shrink-0 ${
            open || activeExtraCount > 0
              ? "bg-[#052460] text-white"
              : "bg-slate-200/60 text-slate-700"
          }`}
          aria-expanded={open}
        >
          Filters{activeExtraCount > 0 ? ` · ${activeExtraCount}` : ""}
        </button>
      </div>

      {open && (
        <div className="mt-3 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <Row label="Device">
            <input
              className="w-full bg-transparent outline-none text-[15px] placeholder:text-slate-400 text-right"
              placeholder="Any"
              value={filters.deviceId ?? ""}
              onChange={(e) => set({ deviceId: e.target.value || undefined })}
            />
          </Row>
          <Row label="Action">
            <select
              className="w-full bg-transparent outline-none text-[15px] text-right appearance-none"
              value={filters.action ?? ""}
              onChange={(e) => set({ action: e.target.value || undefined })}
            >
              <option value="">Any</option>
              {ALL_ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </Row>
          <Row label="From">
            <input
              type="datetime-local"
              className="w-full bg-transparent outline-none text-[15px] text-right"
              value={filters.from ?? ""}
              onChange={(e) => set({ from: e.target.value || undefined })}
            />
          </Row>
          <Row label="To" last>
            <input
              type="datetime-local"
              className="w-full bg-transparent outline-none text-[15px] text-right"
              value={filters.to ?? ""}
              onChange={(e) => set({ to: e.target.value || undefined })}
            />
          </Row>
          {activeExtraCount > 0 && (
            <button
              type="button"
              onClick={() => onChange({ actorId: filters.actorId, after: undefined })}
              className="w-full text-left px-4 py-3 text-[15px] text-red-600 border-t border-slate-200"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  children,
  last,
}: {
  label: string;
  children: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${last ? "" : "border-b border-slate-200"}`}>
      <span className="text-[15px] text-slate-900 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
